import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, Download, Loader2, TrendingUp, TrendingDown, Wallet, Receipt, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import TransactionCorrectionPanel from "@/pages/admin/TransactionCorrectionPanel";
import DepositRecoveryPanel from "@/pages/admin/DepositRecoveryPanel";
import ReconByMethod from "@/components/admin/ReconByMethod";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(preset) {
  const now = new Date();
  const today = iso(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "week") {
    const s = new Date(now); s.setDate(now.getDate() - 6);
    return { from: iso(s), to: today };
  }
  if (preset === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: iso(s), to: today };
  }
  return { from: "", to: "" };
}

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TILES = [
  { key: "gross_collected", label: "Gross collected", icon: TrendingUp, color: "#144F75" },
  { key: "refunds", label: "Refunds", icon: TrendingDown, color: "#B45151" },
  { key: "net_collected", label: "Net collected", icon: Banknote, color: "#5C8A72" },
  { key: "deposits_collected", label: "Deposits collected", icon: Wallet, color: "#B5794E" },
  { key: "outstanding_balances", label: "Outstanding balances", icon: Receipt, color: "#B5794E" },
  { key: "tips", label: "Tips", icon: Wallet, color: "#5C8A72" },
  { key: "enhancer_share", label: "Enhancer earnings", icon: Banknote, color: "#144F75" },
  { key: "ecosystem_share", label: "Ecosystem share", icon: Banknote, color: "#8FA89B" },
  { key: "estimated_stripe_fees", label: "Est. Stripe fees", icon: TrendingDown, color: "#B45151" },
  { key: "ecosystem_net", label: "Ecosystem Net", icon: Banknote, color: "#144F75" },
];

export default function ReconciliationPanel() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation", range.from, range.to],
    queryFn: () => api.get(`/admin/reconciliation?dfrom=${range.from}&dto=${range.to}`).then((r) => r.data),
  });

  const applyPreset = (p) => { setPreset(p); setRange(presetRange(p)); };
  const setCustom = (field, val) => { setPreset("custom"); setRange((r) => ({ ...r, [field]: val })); };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/reconciliation.csv?dfrom=${range.from}&dto=${range.to}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "eyw-reconciliation.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Reconciliation exported");
    } catch { toast.error("We couldn't prepare that export. Please try again."); } finally { setDownloading(false); }
  };

  const t = data?.totals || {};
  const methods = data?.by_payment_method || {};
  const statuses = data?.appointments_by_status || {};

  return (
    <div data-testid="reconciliation-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Banknote size={22} className="text-[#144F75]" /> Reconciliation</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Money view from the revenue ledger — collected, refunds, tips, deposits, outstanding balances, and payment methods. Export matches these totals.</p>
        </div>
        <button onClick={exportCsv} disabled={downloading} data-testid="recon-export" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">
          {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {[["today", "Today"], ["week", "Last 7 days"], ["month", "This month"], ["all", "All time"]].map(([k, lbl]) => (
          <button key={k} onClick={() => applyPreset(k)} data-testid={`recon-preset-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${preset === k ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{lbl}</button>
        ))}
        <span className="mx-1 text-[#D5CEC2]">|</span>
        <input type="date" value={range.from} onChange={(e) => setCustom("from", e.target.value)} data-testid="recon-from" className="eyw-input !py-1.5 text-sm" />
        <span className="text-[#8FA89B] text-sm">to</span>
        <input type="date" value={range.to} onChange={(e) => setCustom("to", e.target.value)} data-testid="recon-to" className="eyw-input !py-1.5 text-sm" />
      </div>

      {isLoading ? (
        <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {TILES.map((tile) => (
              <div key={tile.key} className="eyw-card p-4" data-testid={`recon-tile-${tile.key}`}>
                <div className="flex items-center gap-2 text-xs text-[#8FA89B]"><tile.icon size={14} style={{ color: tile.color }} /> {tile.label}</div>
                <div className="text-2xl font-serif mt-1" style={{ color: tile.color }}>{money(t[tile.key])}</div>
              </div>
            ))}
          </div>

          <ReconByMethod data={data} />

          <div className="grid md:grid-cols-2 gap-4">
            <div className="eyw-card p-5" data-testid="recon-methods">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Payments by method</h3>
              <div className="space-y-2">
                {Object.entries(methods).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm border-b border-[#F1EEE8] pb-1.5">
                    <span className="text-[#4A5D6B] capitalize">{k.replace("_", " ")}</span>
                    <span className="font-semibold text-[#1A242B]">{money(v)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#8FA89B] mt-3">POS tender totals (includes tax/full amounts collected in person).</p>
            </div>

            <div className="eyw-card p-5" data-testid="recon-statuses">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Appointments by status</h3>
              <div className="space-y-2">
                {Object.entries(statuses).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm border-b border-[#F1EEE8] pb-1.5">
                    <span className="text-[#4A5D6B] capitalize">{k.replace("_", " ")}</span>
                    <span className="font-semibold text-[#1A242B]">{v}</span>
                  </div>
                ))}
                {Object.keys(statuses).length === 0 && <p className="text-sm text-[#8FA89B]">No appointments in range.</p>}
              </div>
              <p className="text-[11px] text-[#8FA89B] mt-3">{t.appointments || 0} appointments · {t.ledger_entries || 0} ledger entries · {t.pos_transactions || 0} POS sales</p>
            </div>
          </div>
        </>
      )}
      <DepositRecoveryPanel />
      <TransactionCorrectionPanel />
      <StripeReconciliation />
    </div>
  );
}

function StripeReconciliation() {
  const [days, setDays] = useState(30);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["stripe-recon", days],
    queryFn: () => api.get(`/admin/payouts/stripe-reconciliation?days=${days}`).then((r) => r.data),
    enabled: false,
  });

  const failedPayouts = data?.failed_payouts || [];
  const unlinked = data?.unlinked_revenue_records || [];
  const unmatched = data?.unmatched_stripe_transfers || [];

  return (
    <div className="mt-8" data-testid="stripe-reconciliation">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Banknote size={18} className="text-[#144F75]" /> Stripe reconciliation</h3>
          <p className="text-sm text-[#4A5D6B]">Match internal payout records against Stripe transfers. Flags unreconciled items.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="eyw-input !py-1.5 text-sm" data-testid="recon-days">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={() => refetch()} disabled={isLoading} data-testid="stripe-recon-run" className="eyw-btn !py-2 text-sm">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : "Run check"}
          </button>
        </div>
      </div>

      {data && (
        <div className="space-y-4">
          {/* Summary row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="eyw-card p-4 text-center" data-testid="recon-payout-count"><p className="text-[11px] text-[#8FA89B] uppercase tracking-wide mb-1">Internal payout records</p><p className="font-serif text-2xl text-[#144F75]">{data.payout_record_count}</p></div>
            <div className="eyw-card p-4 text-center" data-testid="recon-stripe-count"><p className="text-[11px] text-[#8FA89B] uppercase tracking-wide mb-1">Stripe transfers found</p><p className="font-serif text-2xl text-[#144F75]">{data.stripe_transfer_count}</p></div>
            <div className={`eyw-card p-4 text-center ${failedPayouts.length + unmatched.length > 0 ? "border-l-4 border-l-[#B45151]" : "border-l-4 border-l-[#5C8A72]"}`} data-testid="recon-issues">
              <p className="text-[11px] text-[#8FA89B] uppercase tracking-wide mb-1">Issues flagged</p>
              <p className={`font-serif text-2xl ${failedPayouts.length + unmatched.length > 0 ? "text-[#B45151]" : "text-[#5C8A72]"}`}>{failedPayouts.length + unmatched.length}</p>
            </div>
          </div>

          {/* Failed payouts */}
          {failedPayouts.length > 0 && (
            <div className="eyw-card p-5" data-testid="recon-failed-payouts">
              <h4 className="font-medium text-[#B45151] flex items-center gap-2 mb-3"><XCircle size={16} /> {failedPayouts.length} Failed payout{failedPayouts.length > 1 ? "s" : ""}</h4>
              <div className="space-y-2">
                {failedPayouts.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm bg-red-50 rounded-lg px-4 py-2.5">
                    <div><span className="font-medium text-[#1A242B]">{r.recipient_name}</span> <span className="text-[#8FA89B] capitalize">· {r.recipient_type}</span></div>
                    <div className="text-right"><span className="text-[#B45151] font-semibold">${Number(r.amount || 0).toFixed(2)}</span><br /><span className="text-[10px] text-[#B45151]">{(r.failure_reason || "").slice(0, 50)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unmatched Stripe transfers */}
          {unmatched.length > 0 && (
            <div className="eyw-card p-5" data-testid="recon-unmatched">
              <h4 className="font-medium text-[#B5794E] flex items-center gap-2 mb-3"><AlertTriangle size={16} /> {unmatched.length} Stripe transfer{unmatched.length > 1 ? "s" : ""} without internal payout record</h4>
              <div className="space-y-2">
                {unmatched.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-[#F9F4EE] rounded-lg px-4 py-2.5">
                    <span className="font-mono text-xs text-[#4A5D6B]">{t.transfer_id}</span>
                    <span className="text-[#B5794E] font-semibold">${Number(t.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unlinked revenue */}
          {unlinked.length > 0 && (
            <div className="eyw-card p-5" data-testid="recon-unlinked">
              <h4 className="font-medium text-[#4A5D6B] flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-[#E0A458]" /> {unlinked.length} revenue record{unlinked.length > 1 ? "s" : ""} missing Stripe PI link</h4>
              <p className="text-xs text-[#8FA89B] mb-3">These transactions were recorded before PI tracking was enabled. They are not missing payments — just not yet linked to a Stripe payment intent ID.</p>
              <div className="overflow-x-auto"><table className="w-full text-xs">
                <thead className="bg-[#F4F1EB]"><tr><th className="text-left px-3 py-2">Service</th><th className="text-left px-3 py-2">Enhancer</th><th className="text-left px-3 py-2">Gross</th><th className="text-left px-3 py-2">Date</th></tr></thead>
                <tbody className="divide-y divide-[#E5E0D8]">
                  {unlinked.slice(0, 20).map((r) => (
                    <tr key={r.id}><td className="px-3 py-2">{r.service_title || r.ref_kind}</td><td className="px-3 py-2">{r.enhancer_name || "—"}</td><td className="px-3 py-2">${Number(r.gross || 0).toFixed(2)}</td><td className="px-3 py-2">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td></tr>
                  ))}
                </tbody>
              </table></div>
              {unlinked.length > 20 && <p className="text-xs text-[#8FA89B] mt-2">+ {unlinked.length - 20} more</p>}
            </div>
          )}

          {failedPayouts.length === 0 && unmatched.length === 0 && (
            <div className="eyw-card p-6 text-center" data-testid="recon-clean">
              <CheckCircle2 size={26} className="mx-auto text-[#5C8A72] mb-2" />
              <p className="text-[#5C8A72] font-medium">All clear — no reconciliation issues in the last {days} days.</p>
            </div>
          )}
        </div>
      )}

      {!data && !isLoading && (
        <div className="eyw-card p-8 text-center text-[#8FA89B]" data-testid="recon-idle">Click "Run check" to compare internal payout records against Stripe transfers.</div>
      )}
    </div>
  );
}
